import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { describeAutomation, type Automation } from '@capture/shared';
import { EmptyState } from '@/components/EmptyState';
import { FormErrorInline } from '@/components/FormErrorInline';
import { useFormConfigStore } from '../../form-config/state/store';
import { usePipelineSteps } from '../../hooks/usePipelineSteps';
import { useJobTypeFields, summarizeFormFields } from '../../hooks/useJobTypeFields';
import { useJobType } from '../../hooks/useJobTypes';
import { useLookupTables } from '../../hooks/useLookupTables';
import { useRoles } from '../../hooks/useRoles';
import { useUsers } from '../../hooks/useUsers';
import { usePanelAutomation } from './usePanelAutomation';
import { IdentitySection } from './IdentitySection';
import { ScopeSection } from './ScopeSection';
import { WhereWhenSection } from './WhereWhenSection';
import { AppliesToSection } from './AppliesToSection';
import { OverrideSection } from './OverrideSection';
import { BranchesSection } from './BranchesSection';
import { TryItSection } from './TryItSection';
import styles from './panel.module.css';

/**
 * A3 panel — the full-page edit surface for one automation (assembled from
 * the RuleAttachments it compiles down to). Mounts the Task 8 section stack
 * in design order: Identity, Scope, Where & when, Applies to, Override.
 * Task 9 adds the Branches editor below Override.
 */
export function AutomationPanelPage() {
  const { jobTypeId = '', automationKey = '' } = useParams<{
    jobTypeId: string;
    automationKey: string;
  }>();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('form') ?? '';

  const { automation, setAutomation, save, orphan, saveError } = usePanelAutomation(automationKey, formId);

  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const versionId = useFormConfigStore((s) => s.version?.id ?? null);
  const { steps: pipelineStepRows } = usePipelineSteps(jobTypeId);
  const pipelineSteps = useMemo(
    () => pipelineStepRows.map((s) => ({ stepType: s.stepType, label: s.label })),
    [pipelineStepRows],
  );
  const { fields: jobTypeFields } = useJobTypeFields(jobTypeId);
  const { jobType } = useJobType(jobTypeId);
  const { tables: lookupTables } = useLookupTables();
  // Notify's role picker offers the tenant's REAL roles; it writes the stable
  // slug and displays the name (rename-safe — see role-slug.ts).
  const { roles: tenantRoles } = useRoles();
  const roleOptions = useMemo(
    () => (tenantRoles ?? []).map((r) => ({ slug: r.slug, name: r.name })),
    [tenantRoles],
  );
  // Notify's "also send email" target-user picker offers the tenant's active
  // users (Task 13) — writes the user's id, displays "Name (email)".
  const { users: tenantUsers } = useUsers();
  const userOptions = useMemo(
    () => tenantUsers.map((u) => ({ id: u.id, name: u.name, email: u.email })),
    [tenantUsers],
  );
  // P8 — job-level constant names for the condition value picker ("A named
  // constant…"). Read straight off the draft V3 content in the store.
  const constantNames = useMemo(() => Object.keys(contentV3?.constants ?? {}), [contentV3?.constants]);
  // E-054 P5 follow-up — the reclassify direct-target picker offers the job's
  // REAL form classifications (option value = classificationKey, the same key
  // the publish gate `rule.action.reclassify.unknown-classification` and the
  // runtime `document.classification` resolve against), labeled by form name.
  const classificationOptions = useMemo(
    () =>
      (contentV3?.forms ?? [])
        .filter((f) => f.classificationKey)
        .map((f) => ({ key: f.classificationKey, label: f.name || f.classificationKey })),
    [contentV3?.forms],
  );
  // P2b — line-item groups feed the row_check editor (rowGroup / highlightColumn
  // selects + the condition field picker's dotted `${groupId}.${col}` refs).
  const lineItemGroups = useMemo(
    () =>
      (jobType?.lineItemDefinitions ?? []).map((d) => ({
        groupId: d.groupId,
        name: d.name,
        columns: (Array.isArray(d.columns) ? d.columns : [])
          .map((c) =>
            c && typeof c === 'object' && typeof (c as { name?: unknown }).name === 'string'
              ? (c as { name: string }).name
              : '',
          )
          .filter(Boolean),
      })),
    [jobType],
  );
  // F-056 — every field vocabulary on this page (scope picker, branch IF
  // sources, config-body field pickers) is the OWNER FORM's field bindings,
  // never the job-type union across forms (`useJobTypeFields` reads the
  // flattened legacy content, which merges every form). The union is offered
  // only while the V3 content hasn't loaded yet, so the pickers aren't empty
  // before that data arrives.
  const ownerForm = contentV3?.forms.find((f) => f.id === formId);
  const formFieldSummaries = useMemo(
    () => (ownerForm ? summarizeFormFields(ownerForm.fields) : jobTypeFields),
    [ownerForm, jobTypeFields],
  );
  const scopeFields = useMemo(
    () => formFieldSummaries.map((f) => ({ name: f.name, label: f.label || f.name })),
    [formFieldSummaries],
  );

  const backHref = `/admin/job-types/${jobTypeId}?tab=automations`;

  if (!automation) {
    return (
      <div className={styles.page}>
        <Link to={backHref} className={styles.backLink}>
          ← Back to Automations
        </Link>
        <EmptyState
          title={orphan ? "Can't edit this automation" : "We couldn't find that automation"}
          body={
            orphan
              ? 'This rule could not be parsed into the automation model. Edit it from the Automations list.'
              : 'It may have been deleted, or the link is out of date.'
          }
        />
      </div>
    );
  }

  // Every section shares this handler: update the local draft immediately,
  // then commit to the store — unless the name is blank, in which case
  // `save()` is withheld (IdentitySection surfaces the inline block) so an
  // in-progress edit never persists an unnamed automation.
  function handleChange(next: Automation) {
    setAutomation(next);
    if (!next.name.trim()) return;
    save(next);
  }

  return (
    <div className={styles.page}>
      <Link to={backHref} className={styles.backLink}>
        ← Back to Automations
      </Link>

      {/* E-071 (driver-directed) — name the surface on screen so the UI and the
          people talking about it share one vocabulary. */}
      <div className={styles.surfaceLabel}>Panel editor</div>

      <div className={styles.pinned} data-testid="pinned-sentence">
        {describeAutomation(automation)}
      </div>

      {saveError && <FormErrorInline error={new Error(saveError)} />}

      <div className={styles.sections}>
        {/* Keyed on the route's automation identity so IdentitySection's local
            name/description draft (see IdentitySection.tsx) re-seeds instead
            of leaking text when the operator navigates to a different
            automation without this page unmounting. */}
        <IdentitySection key={automationKey} automation={automation} onChange={handleChange} />
        <ScopeSection
          automation={automation}
          fields={scopeFields}
          onChange={handleChange}
          ownerFormLabel={(contentV3?.forms.length ?? 0) > 1 ? ownerForm?.name : undefined}
        />
        <WhereWhenSection automation={automation} pipelineSteps={pipelineSteps} onChange={handleChange} />
        <AppliesToSection automation={automation} onChange={handleChange} roles={roleOptions} />
        <OverrideSection automation={automation} onChange={handleChange} roles={roleOptions} />
        <BranchesSection
          automation={automation}
          onChange={handleChange}
          fields={formFieldSummaries}
          lineItemGroups={lineItemGroups}
          lookupTables={lookupTables}
          constants={constantNames}
          roles={roleOptions}
          users={userOptions}
          classifications={classificationOptions}
          pipelineSteps={pipelineSteps}
        />
        <TryItSection
          automation={automation}
          formFields={scopeFields}
          stage={automation.stages?.[0]}
          versionId={versionId}
        />
      </div>
    </div>
  );
}
